/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';

import * as AppPropTypes from '../lib/PropTypes';

import { useApi } from './ApiContext';
import { useUser } from './AuthContext';

const OnlineFilmsContext = React.createContext(null);

const useOnlineFilmsContext = () => useContext(OnlineFilmsContext);

export const useOnlineFilmItems = () => {
    const { onlineFilms } = useOnlineFilmsContext();
    return onlineFilms;
};

export const useSetOnlineFilms = () => {
    const { setOnlineFilms } = useOnlineFilmsContext();
    return setOnlineFilms;
};

export const getOnlineFilmByFilm = (film) => {
    const { id: filmId = null } = film || {};
    const onlineFilms = useOnlineFilmItems();
    const item =
        (onlineFilms || []).find(
            (onlineFilm = null) => onlineFilm !== null && onlineFilm.filmId === filmId,
        ) || null;
    return item;
};

export const useAddOnlineFilm = () => {
    const setOnlineFilms = useSetOnlineFilms();
    const onlineFilms = useOnlineFilmItems();
    return useCallback(
        (onlineFilm) => {
            if (onlineFilm !== null) {
                setOnlineFilms([
                    ...(onlineFilms || []).filter((it) => it.id !== onlineFilm.id),
                    onlineFilm,
                ]);
            }
        },
        [onlineFilms, setOnlineFilms],
    );
};

const propTypes = {
    onlineFilms: AppPropTypes.onlineFilms,
    children: PropTypes.node.isRequired,
};

const defaultProps = {
    onlineFilms: null,
};

export const OnlineFilmsProvider = ({ onlineFilms: initialOnlineFilms, children }) => {
    const [onlineFilms, setOnlineFilms] = useState(initialOnlineFilms);
    const api = useApi();
    const user = useUser();
    const { id = null } = user || {};

    useEffect(() => {
        if (user !== null && initialOnlineFilms === null) {
            api.onlineFilms.get().then((response) => {
                setOnlineFilms(response);
            });
        }
    }, [id, initialOnlineFilms, api, setOnlineFilms]);

    useEffect(() => {
        if (user !== null) {
            setOnlineFilms(null);
        }
    }, [setOnlineFilms]);

    const value = useMemo(
        () => ({
            onlineFilms,
            setOnlineFilms,
        }),
        [onlineFilms, setOnlineFilms],
    );
    return <OnlineFilmsContext.Provider value={value}>{children}</OnlineFilmsContext.Provider>;
};

OnlineFilmsProvider.propTypes = propTypes;
OnlineFilmsProvider.defaultProps = defaultProps;

export default OnlineFilmsContext;
